// /src/router.jsx

import {
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import React, { lazy, Suspense } from "react";
import LoadingSpinner from "./components/LoadingSpinner";

import App from "./App";

// Pages publiques
import HomePage from "./pages/HomePage";
import StructurePage from "./pages/StructurePage";
import InfoPage from "./pages/Info";
import JoinUsPage from "./pages/JoinUsPage";
import LoginPage from "./pages/LoginPage";
import PrivacyPage from "./pages/PrivacyPage";
import LegalPage from "./pages/LegalPage";
import GcuPage from "./pages/GcuPage";
import ContactPage from "./pages/ContactPage";
import Unauthorized from "./pages/unauthorized";
import UnderMaintenance from "./pages/UnderMaintenance";

// Pages de vérification
import ActivationPage from "./pages/confirm/ActivationPage";
import ConfirmPage from "./pages/confirm/ConfirmPage";
import EmailChangeValidation from "./pages/confirm/EmailChangeValidation";
import ResetPage from "./pages/confirm/ResetPage";
import IdentityCheck from "./pages/GamerIdentityCheck";
import LoginDiscordMfa from "./pages/LoginDiscordMfa";

// Privé
import PersonalHub from "./pages/private/members/personalhub";
import MembersOnboardingLayout from "./pages/private/members/MembersOnboardingLayout";
import MembersOnboardingWelcome from "./pages/private/members/MembersOnboardingWelcome";
import MembersOnboardingSteps from "./pages/private/members/MembersOnboardingSteps";
import MembersOnboardingEnding from "./pages/private/members/MembersOnboardingEnding";
const Roster = lazy(() => import("./pages/private/members/Roster"));
import Calendar from "./pages/private/members/Calendar";
import Academy from "./pages/private/members/Academy";
import UserSettings from "./pages/private/settings/Settings";
import AdminPanel from "./pages/private/admin/AdminPanel";
import PrivateRoute from "./PrivateRoute";

// Profil utilisateur
import Profile from "./pages/private/profile/Profile";
import Hangar from "./pages/private/members/personalhub/Hangar";

const withMaintenance = (Component, isUnderMaintenance, isLoggedIn) => {
  return isUnderMaintenance && !isLoggedIn ? <UnderMaintenance /> : Component;
};

export const createRouter = ({ isLoggedIn, isUnderMaintenance, setIsLoggedIn, setEmail }) =>
  createBrowserRouter(
    [
      {
        path: "/",
        element: <App />,
        children: [
          // Public pages
          { path: "/", element: withMaintenance(<HomePage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/structure", element: withMaintenance(<StructurePage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/info", element: withMaintenance(<InfoPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/join", element: withMaintenance(<JoinUsPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/privacy", element: withMaintenance(<PrivacyPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/legal", element: withMaintenance(<LegalPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/gcu", element: withMaintenance(<GcuPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/contact", element: withMaintenance(<ContactPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/unauthorized", element: withMaintenance(<Unauthorized />, isUnderMaintenance(), isLoggedIn) },

          { path: "/login", element: <LoginPage setIsLoggedIn={setIsLoggedIn} setEmail={setEmail} /> },

          // Identification and checks
          { path: "/activation", element: withMaintenance(<ActivationPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/confirm", element: withMaintenance(<ConfirmPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/emailchange/:token", element: withMaintenance(<EmailChangeValidation />, isUnderMaintenance(), isLoggedIn) },
          { path: "/reset/:token", element: withMaintenance(<ResetPage />, isUnderMaintenance(), isLoggedIn) },
          { path: "/LoginDiscordMfa", element: <LoginDiscordMfa /> },

          {
            path: "/GamerIdentityCheck",
            element: isUnderMaintenance()
              ? <UnderMaintenance />
              : isLoggedIn
              ? <IdentityCheck />
              : <Navigate to="/login" replace />,
          },

          // Members — onboarding: welcome (index) then steps
          {
            path: "/members/onboarding",
            element: <MembersOnboardingLayout />,
            children: [
              { index: true, element: <MembersOnboardingWelcome /> },
              { path: "steps", element: <MembersOnboardingSteps /> },
              { path: "ending", element: <MembersOnboardingEnding /> },
            ],
          },
          { path: "/members/personalhub/:section?", element: <PrivateRoute><PersonalHub /></PrivateRoute> },
          { path: "/members/dashboard", element: <Navigate to="/members/personalhub" replace /> },
          {
            path: "/members/roster",
            element: (
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <Roster />
                </Suspense>
              </PrivateRoute>
            ),
          },
          { path: "/members/calendar", element: <PrivateRoute><Calendar /></PrivateRoute> },
          { path: "/members/calendar/auctions/:occurrenceId", element: <PrivateRoute><Calendar /></PrivateRoute> },
          { path: "/members/academy", element: <PrivateRoute><Academy /></PrivateRoute> },

          // User settings
          { path: "/settings/*", element: <PrivateRoute><UserSettings /></PrivateRoute> },

          // User profile
          { path: "/profile/*", element: <PrivateRoute><Profile /></PrivateRoute> },

          // Hangar
          { path: "/profile/*", element: ( <PrivateRoute><Profile /></PrivateRoute> ),
            children: [
              { path: "hangar", element: <Hangar /> },
            ],
          },

          // Admin
          { path: "/admin/*", element: <PrivateRoute><AdminPanel /></PrivateRoute> },

        ],
      },
    ],
    {
      future: {
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      },
    }
  );
